import React, { useState } from "react";
import DateField from "../../../common/formFields/DateField";
import { INVALID_DATE } from "../../../common/constants/ApiConstants";
import {
    dateFieldValueToString,
    INVALID_DATE_MESSAGE,
    isCompleteCalendarDate,
    QuickEntryFieldKind,
    sanitizeFieldInput,
} from "./quickEntryFieldConstraints";
import { useModalFocus } from "./useModalFocus";
import { useModalDrag } from "./useModalDrag";
import "./CreateEntityFormModal.scss";

export interface CreateEntityField {
    readonly name: string;
    readonly label: string;
    /** Pre-fill value (AI-extracted intake data - the specialist edits before saving). */
    readonly value?: string;
    /** Required fields block Save until filled (mirrors the classic create form's required set). */
    readonly required?: boolean;
    /** Rendered full-width on its own row (e.g. an address line) instead of the two-column grid. */
    readonly fullWidth?: boolean;
    /** Placeholder hint (matches the classic add-new form, e.g. "XXX-XXX-XXXX" for fax). */
    readonly placeholder?: string;
    /** Max input length (matches the classic add-new form's field caps). */
    readonly maxLength?: number;
    /**
     * Values for a `type: "select"` field, in the order they are offered. The renderer prepends
     * Classic's unselectable "- Select -" placeholder, so this list holds real values only.
     *
     * AB#35253: added for the requestor State field, whose Classic counterpart is a dropdown over
     * UNITED_STATES (RequestorContactPanel.tsx ~543-558). Label and value are the same string
     * because that is what both panes offer today (Bill To State does likewise,
     * QuickEntryBillingPane.tsx ~925); a field needing them to differ can widen this then.
     */
    readonly options?: readonly string[];
    /**
     * Input kind. "date" renders the shared masked DateField (MM/DD/YYYY
     * auto-format on type, same primitive classic uses) instead of a plain text
     * input, so e.g. the create-new-patient DOB field masks like classic.
     *
     * "select" (AB#35253) renders a dropdown over `options` instead of an input, which is how a
     * field stops accepting the wrong character CLASS entirely rather than filtering keystrokes.
     *
     * Every other kind routes keystrokes through the shared sanitizeFieldInput
     * (quickEntryFieldConstraints.ts), which filters as the specialist types rather
     * than complaining after the fact - what the classic pane's masked fields already
     * do. InlineSearchForm calls the SAME function, so a field spelled "phone"
     * behaves identically in the create form, the edit form and the inline search row.
     *
     * "phone" (AB#35154) masks digits into XXX-XXX-XXXX via the shared normalizePhone -
     * the SAME normalizer the classic pane applies to its own phone/fax fields
     * (ReduxFormTextField, gated on PHONE_FIELDS). Without it these inputs wrote
     * through raw, so the same field spelled the same way behaved differently
     * depending on which pane the specialist opened it from.
     * City, State and Zip declare no transform kind at all (AB#35253): each carries a cap plus a
     * post-hoc `pattern`, because masking Zip destroyed non-US postal codes and masking City
     * misspelled real ones - see quickEntryFieldConstraints.
     *
     * Defaults to "text".
     */
    readonly type?: QuickEntryFieldKind;
    /**
     * Starts a titled section at this field (rendered full-width above it), so one form
     * can group distinct blocks - e.g. the requestor's org+address followed by a
     * "Contact" block. Omitted on every field => one ungrouped grid, as before.
     */
    readonly section?: string;
    /**
     * POST-HOC format validator (AB#37970 / AB#37973). When the box is non-empty and fails
     * this regex the field shows `invalidMessage` and Save is disabled - the specialist is told
     * their entry is wrong and can fix it. Deliberately NOT a keystroke transform: `type` masks
     * as you type, this judges what was typed. Empty is left to `required`, so an untouched
     * optional field never shows a format error.
     *
     * This is what brings the non-redux-form Quick Entry modals to Classic parity. Classic's
     * fields run localValidations' regexes because they are redux-form fields; these are not, so
     * without a declaration here a value Classic refuses saved silently (PR !9947 thread 53163).
     * Pass Classic's own regex + message - CLASSIC_PHONE_REGEX / CLASSIC_ZIP_REGEX and their
     * INVALID_* messages in quickEntryFieldConstraints - never a locally re-typed one.
     */
    readonly pattern?: RegExp;
    /** Message shown when `pattern` fails. Required for `pattern` to have any visible effect. */
    readonly invalidMessage?: string;
}

export interface CreateEntityFormModalProps {
    readonly title: string;
    readonly subtitle?: string;
    readonly fields: readonly CreateEntityField[];
    readonly submitLabel: string;
    /**
     * Save (F1c no-match create): returns the (specialist-edited) values keyed by
     * field name. This does NOT persist to the database - it writes the values
     * into the data-entry form so the transaction's final Save persists them,
     * matching the "create new" no-match path (AC9).
     */
    readonly onSubmit: (values: Readonly<Record<string, string>>) => void;
    readonly onClose: () => void;
}

/**
 * Create-new entity form modal (Spec #34101 (F1c), AC9 no-match path). Opened
 * from the "No Match - Create New" affordance / the results grid's "+ Create
 * New" button when a patient/requestor search yields no usable match. Renders
 * the same fields the intake extraction captures; on Save it hands the (edited)
 * values back so the caller writes them into the data-entry form (no DB write
 * here - the transaction's final Save persists them). Generic over its field
 * list so the patient and requestor create forms share one implementation.
 *
 * AB#38824: this component pre-fills from whatever `field.value` it is handed and has no
 * concept of a match - WHETHER the extracted values are handed to it is the calling row's
 * decision (createPrefill.ts), because only the row knows whether the database already holds
 * the person. Three callers depend on that split, and one of them (AssignContactModal) must
 * keep pre-filling unconditionally.
 *
 * PHI note (hipaa-compliance.md): field values are patient/requestor PII - this
 * component never logs them.
 */

/**
 * AB#35253: a `select` field's pre-fill, reconciled with its own option list.
 *
 * Case-insensitive and trimmed, snapping to the option's own spelling, so a correct extraction
 * spelled "ny" is not thrown away like a wrong one. An UNMATCHED value is KEPT rather than
 * discarded, and selectOptionsFor renders it: `tblRequestorName.State` is a varchar(3) that predates
 * any dropdown, and discarding a record's unlisted code opened the form with a blank box and a
 * permanently greyed Save, which made an ADDRESS-only correction to that requestor unsaveable.
 * Classic does the same - its redux-form dropdown holds the record's value whether or not
 * UNITED_STATES offers it. A control must not discard the value it was handed.
 */
const initialFieldValue = (field: CreateEntityField): string => {
    const value = field.value ?? "";
    if (!field.options) {
        return value;
    }
    const trimmed = value.trim();
    return field.options.find(option => option.toUpperCase() === trimmed.toUpperCase()) ?? trimmed;
};

/**
 * The options a select actually renders: its own list, plus the CURRENT value when the list does not
 * offer it (see initialFieldValue). Prepended, so the value the record holds is visible in the box
 * instead of hidden behind a blank one - strictly more honest than Classic, which keeps such a value
 * but shows nothing. Choosing a listed option replaces it and it drops out of the list.
 */
const selectOptionsFor = (options: readonly string[], current: string): readonly string[] => (
    current && options.indexOf(current) < 0 ? [current, ...options] : options
);

/**
 * Round-5 review: mirrors MatchGridModal's SUBTITLE_ID pattern, so the subtitle
 * ("Enter the contact's details..." from AssignContactModal.tsx, or the
 * per-consumer equivalent) is announced once on open instead of never. A
 * constant, not a per-instance counter, for the same reason MatchGridModal's is:
 * two CreateEntityFormModal instances never mount simultaneously.
 */
const SUBTITLE_ID = "create-entity-modal__subtitle";

const CreateEntityFormModal: React.FC<CreateEntityFormModalProps> = (props) => {
    const { ref: dialogRef, onKeyDown } = useModalFocus(props.onClose);
    // AB#38825: the specialist transcribes this form FROM the request document, and the dialog
    // opened centred on top of it. Dragging by the header moves it aside. Shares useModalFocus's
    // ref rather than taking its own - one dialog root, measured by the clamp, focus-managed by
    // the other hook, with neither writing the other's state.
    const { offset, onHandleMouseDown, dragging } = useModalDrag(dialogRef);
    const [values, setValues] = useState<Record<string, string>>(() =>
        Object.fromEntries(props.fields.map(field => [field.name, initialFieldValue(field)])),
    );

    /**
     * AB#37980: same defect, second door. This form's DOB is the other `type: "date"`
     * entry point on the Patient Info row, and it swallowed a rejected date exactly as
     * the inline search used to: dateFieldValueToString collapses INVALID_DATE to "",
     * errMsg was hardcoded to "", so the typed text vanished from the box and - because
     * DOB is `required` - Save silently greyed out with nothing saying why. Same state,
     * same message, same shared predicate as InlineSearchForm, so a date rejected in one
     * form is rejected identically in the other.
     */
    const [dateErrors, setDateErrors] = useState<Record<string, boolean>>({});

    const onFieldChange = (name: string, value: string) => {
        setValues(prev => ({ ...prev, [name]: value }));
    };

    const onDateFieldChange = (name: string, value?: Date | string) => {
        setDateErrors(prev => ({ ...prev, [name]: value === INVALID_DATE }));
        onFieldChange(name, dateFieldValueToString(value));
    };

    // Blur is the only channel that sees a complete-looking but non-existent date
    // ("02/30/1996") - see isCompleteCalendarDate and InlineSearchForm.onDateFieldBlur.
    // Clearing matters here too: the picker never fired, so values[name] would otherwise
    // still hold the AI-extracted pre-fill the specialist had just overtyped, and Save
    // would persist THAT onto the new patient.
    const onDateFieldBlur = (name: string, text: string) => {
        const valid = isCompleteCalendarDate(text);
        setDateErrors(prev => ({ ...prev, [name]: !valid }));
        if (!valid) {
            onFieldChange(name, "");
        }
    };

    const missingRequired = props.fields.some(
        field => field.required && !(values[field.name] ?? "").trim(),
    );

    /**
     * AB#37970 / AB#37973: which fields hold a non-empty value their `pattern` refuses. Computed
     * declaratively on every render rather than latched on blur, so the error appears and clears
     * as the value does and can never disagree with the Save gate below.
     */
    const formatErrors: Readonly<Record<string, string>> = Object.fromEntries(
        props.fields
            .filter(field => {
                const entered = (values[field.name] ?? "").trim();
                return field.pattern && field.invalidMessage && entered.length > 0 && !field.pattern.test(entered);
            })
            .map(field => [field.name, field.invalidMessage as string]),
    );
    const hasFormatError = Object.keys(formatErrors).length > 0;

    const onSave = () => {
        if (missingRequired || hasFormatError) {
            return;
        }
        props.onSubmit(values);
    };

    return (
        <>
            {/*
              * AB#38825: kept and still pointer-absorbing, but neither tinted nor click-to-close.
              *
              * WHY THE TINT AND THE onClick WENT. The point of AB#38825 is to let the specialist
              * read the request document while filling this form. A 35% scrim over it is not
              * "referring back to the document", and a click-to-close on the very thing they are
              * trying to read destroyed a part-typed form. Escape (useModalFocus), Cancel and the
              * header X all still close it, so the dialog keeps its three deliberate exits and
              * loses only the accidental one.
              *
              * WHY IT STILL ABSORBS POINTER INPUT. Two reasons, and an earlier revision of this
              * fix made it `pointer-events: none` and broke both.
              *   1. It is the drag shield. The request document renders in a PDFTron IFRAME, and
              *      mouse events over an iframe go to the iframe's own document - the `mousemove`
              *      listener useModalDrag puts on THIS document stops firing the moment the
              *      pointer crosses it, stranding the dialog mid-drag.
              *   2. This dialog is `aria-modal`, and the page behind it holds controls that
              *      discard the transaction (Confirm All, Next request). An inert backdrop left
              *      every one of them clickable underneath an open form, so a mis-aimed click
              *      could throw away exactly the work the specialist was in the middle of typing.
              * Transparent is what the bug asked for; inert is not the same thing, and is not
              * what it asked for.
              */}
            <div
                className={`create-entity-modal__backdrop${dragging ? " create-entity-modal__backdrop--dragging" : ""}`}
                aria-hidden="true"
            />
            <div
                ref={dialogRef}
                className={`create-entity-modal${dragging ? " create-entity-modal--dragging" : ""}`}
                // AB#38825: composed into the SAME transform the stylesheet centres with
                // (translate(-50%, -50%)), so the dialog never jumps by half its own size on the
                // first mousedown. {x:0,y:0} reproduces the stylesheet exactly.
                // Omitted entirely at the un-dragged origin, so a modal nobody has moved renders
                // byte-identical to before this change and inherits the stylesheet's transform.
                style={offset.x === 0 && offset.y === 0
                    ? undefined
                    : { transform: `translate(calc(-50% + ${offset.x}px), calc(-50% + ${offset.y}px))` }}
                role="dialog"
                aria-modal="true"
                aria-label={props.title}
                // Round-5 review: mirrors MatchGridModal's dialog-root aria-describedby -
                // the subtitle is a dialog-level instruction, announced once on open.
                // Omitted, not pointed at a nonexistent id, when there is no subtitle.
                aria-describedby={props.subtitle ? SUBTITLE_ID : undefined}
                tabIndex={-1}
                onKeyDown={onKeyDown}
            >
                <div className="create-entity-modal__header" onMouseDown={onHandleMouseDown}>
                    <div>
                        <h2 className="create-entity-modal__title">{props.title}</h2>
                        {props.subtitle ? (
                            <p id={SUBTITLE_ID} className="create-entity-modal__subtitle">{props.subtitle}</p>
                        ) : null}
                    </div>
                    <button type="button" className="create-entity-modal__close" aria-label="Close" onClick={props.onClose}>
                        &times;
                    </button>
                </div>

                <div className="create-entity-modal__grid">
                    {props.fields.map(field => {
                        const isDate = field.type === "date";
                        // DateField renders its own <Form>; a <form> inside a <label> is invalid HTML, so date fields use a <div> wrapper.
                        const Wrapper = isDate ? "div" : "label";
                        return (
                            <React.Fragment key={field.name}>
                            {field.section ? (
                                <div className="create-entity-modal__section">{field.section}</div>
                            ) : null}
                            <Wrapper
                                className={`create-entity-modal__field${field.fullWidth ? " create-entity-modal__field--full" : ""}`}
                            >
                                <span className="create-entity-modal__label">
                                    {field.label}
                                    {field.required ? <span className="create-entity-modal__required"> *</span> : null}
                                </span>
                                {field.type === "select" ? (
                                    <select
                                        className="form-control create-entity-modal__input"
                                        data-field={field.name}
                                        value={values[field.name] ?? ""}
                                        onChange={event => onFieldChange(field.name, event.target.value)}
                                    >
                                        {/* Classic's own first option - unselectable, so the
                                            placeholder can never be submitted as a value. */}
                                        <option value="" disabled hidden>- Select -</option>
                                        {selectOptionsFor(field.options ?? [], values[field.name] ?? "").map(option => (
                                            <option key={option} value={option}>{option}</option>
                                        ))}
                                    </select>
                                ) : isDate ? (
                                    <DateField
                                        placeholder={field.placeholder ?? "mm/dd/yyyy"}
                                        errMsg={dateErrors[field.name] ? INVALID_DATE_MESSAGE : ""}
                                        className="create-entity-modal__input"
                                        value={values[field.name] || undefined}
                                        onChange={value => onDateFieldChange(field.name, value)}
                                        onBlur={event => onDateFieldBlur(field.name, event.target.value)}
                                    />
                                ) : (
                                    <input
                                        type="text"
                                        className="form-control create-entity-modal__input"
                                        data-field={field.name}
                                        placeholder={field.placeholder}
                                        maxLength={field.maxLength}
                                        value={values[field.name] ?? ""}
                                        onChange={event => onFieldChange(
                                            field.name,
                                            // AB#35154: mask phone/fax as they type, using the classic pane's own
                                            // normalizePhone so both panes format identically - including the
                                            // "x" extension form past 10 digits. It is idempotent (strips
                                            // non-digits before rebuilding), so re-editing an already-masked
                                            // value never double-dashes.
                                            sanitizeFieldInput(field.type, event.target.value),
                                        )}
                                    />
                                )}
                                {formatErrors[field.name] ? (
                                    <span className="create-entity-modal__field-error" role="alert">
                                        {formatErrors[field.name]}
                                    </span>
                                ) : null}
                            </Wrapper>
                            </React.Fragment>
                        );
                    })}
                </div>

                <div className="create-entity-modal__actions">
                    <button type="button" className="btn create-entity-modal__cancel" onClick={props.onClose}>
                        Cancel
                    </button>
                    <button
                        type="button"
                        className="btn create-entity-modal__save"
                        disabled={missingRequired || hasFormatError}
                        onClick={onSave}
                    >
                        {props.submitLabel}
                    </button>
                </div>
            </div>
        </>
    );
};

export default CreateEntityFormModal;
